import { supabase } from "@/integrations/supabase/client";

export type TotpFactor = { id: string; friendlyName: string };

const DEFAULT_DEVICE_NAME = "Authenticator App";

export const toTotpFactors = (factors: { id: string; friendly_name?: string }[]): TotpFactor[] =>
  factors.map((factor) => ({ id: factor.id, friendlyName: factor.friendly_name ?? DEFAULT_DEVICE_NAME }));

const LAST_FACTOR_KEY = "mfa:last-factor-id";

const readRememberedFactorId = () => {
  try {
    return localStorage.getItem(LAST_FACTOR_KEY);
  } catch {
    return null;
  }
};

export const rememberFactorId = (factorId: string) => {
  try {
    localStorage.setItem(LAST_FACTOR_KEY, factorId);
  } catch {
    // Remembering is a convenience; sign-in works without it.
  }
};

export const pickInitialFactorId = (factors: TotpFactor[]) => {
  const remembered = readRememberedFactorId();
  if (remembered && factors.some((factor) => factor.id === remembered)) return remembered;

  return factors[0]?.id ?? "";
};

export const suggestDeviceName = (existingNames: string[]) => {
  const taken = new Set(existingNames.map((name) => name.toLowerCase()));
  if (!taken.has(DEFAULT_DEVICE_NAME.toLowerCase())) return DEFAULT_DEVICE_NAME;

  let n = 2;
  while (taken.has(`${DEFAULT_DEVICE_NAME} ${n}`.toLowerCase())) n += 1;

  return `${DEFAULT_DEVICE_NAME} ${n}`;
};

export const verifyTotpCode = async ({ factorId, code }: { factorId: string; code: string }) => {
  try {
    const challenge = await supabase.auth.mfa.challenge({ factorId });
    if (challenge.error) return { error: challenge.error.message };

    const verify = await supabase.auth.mfa.verify({ factorId, challengeId: challenge.data.id, code });

    return { error: verify.error?.message ?? null };
  } catch (err: unknown) {
    return { error: err instanceof Error ? err.message : "Verification failed" };
  }
};
